import { useEffect } from 'react';
import type { Address } from 'viem';

import { subscribeWhileMounted } from '@/lib/hyperliquid/client';

import { perpsAccountSelector, usePerpsExchangeStore } from '@/store/perps/exchange';

/**
 * Streams a wallet's HyperCore spot balances and per-dex perps states for read-only views
 * (the portfolio). Unlike `usePerpsAccountFeed` it needs no connected wallet, agent, or
 * fill toasts, and it keeps every dex regardless of collateral. Mount once per page, and not
 * alongside `usePerpsAccountFeed`: both drop the user's account from the store on unmount.
 */
export function useHyperCoreAccountFeed(user: Address | undefined) {
  useEffect(() => {
    if (!user) return;
    let disposed = false;
    const store = usePerpsExchangeStore.getState();
    const cleanups = [
      subscribeWhileMounted((client, options) =>
        client.spotState(
          { user },
          (event) => {
            if (!disposed && !options.signal?.aborted) store.setSpotState(user, event.spotState);
          },
          options,
        ),
      ),
      subscribeWhileMounted((client, options) =>
        client.allDexsClearinghouseState(
          { user },
          (event) => {
            if (!disposed && !options.signal?.aborted)
              store.setAccountStates(user, event.clearinghouseStates);
          },
          options,
        ),
      ),
    ];
    return () => {
      disposed = true;
      cleanups.forEach((cleanup) => cleanup());
      store.removeAccount(user);
    };
  }, [user]);

  return usePerpsExchangeStore(perpsAccountSelector(user));
}
